import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { PERMISSION_DECISIONS } from './runAgentTurn';
import { diffWords } from './textDiff';

const DECIDED = {
  [PERMISSION_DECISIONS.once]: { label: 'Allowed', color: '#067647', background: '#ecfdf3' },
  [PERMISSION_DECISIONS.always]: { label: 'Always allowed', color: '#067647', background: '#ecfdf3' },
  [PERMISSION_DECISIONS.deny]: { label: 'Denied', color: '#b42318', background: '#fef3f2' },
};

const styles = {
  card: {
    border: '1px solid #e4e7ec',
    borderRadius: '10px',
    background: '#fff',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
    fontSize: '0.84rem',
    color: '#101828',
    overflow: 'hidden',
  },
  header: { padding: '8px 10px', display: 'flex', alignItems: 'flex-start', gap: '8px' },
  title: { flex: 1, minWidth: 0, fontWeight: 600, lineHeight: 1.3 },
  meta: { fontSize: '0.72rem', fontWeight: 400, color: '#667085', marginTop: '1px', lineHeight: 1.35 },
  pill: (decided) => ({
    flex: 'none',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: decided.color,
    background: decided.background,
    borderRadius: '999px',
    padding: '2px 8px',
  }),
  body: { padding: '0 10px 8px', display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '0.7rem', fontWeight: 600, color: '#475467', marginBottom: '2px' },
  diff: {
    padding: '6px 8px',
    borderRadius: '6px',
    background: '#f9fafb',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '150px',
    overflowY: 'auto',
    fontSize: '0.8rem',
    lineHeight: 1.45,
    color: '#344054',
  },
  chip: { fontSize: '0.7rem', background: '#f2f4f7', color: '#344054', borderRadius: '999px', padding: '1px 8px' },
  selectableChange: { padding: '7px 0', borderTop: '1px solid #f2f4f7' },
  changeHeader: { display: 'flex', alignItems: 'flex-start', gap: '8px' },
  changeSummary: { flex: 1, minWidth: 0, fontSize: '0.8rem', lineHeight: 1.4 },
  toggleChange: {
    border: 0,
    background: 'transparent',
    color: '#475467',
    cursor: 'pointer',
    flex: 'none',
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '1px 0',
  },
  selectableChip: (selected) => ({
    border: `1px solid ${selected ? '#2dce89' : '#d0d5dd'}`,
    background: selected ? '#ecfdf3' : '#fff',
    color: selected ? '#067647' : '#667085',
    borderRadius: '999px',
    cursor: 'pointer',
    fontSize: '0.7rem',
    fontWeight: 600,
    padding: '2px 8px',
    textDecoration: selected ? 'none' : 'line-through',
  }),
  footer: { display: 'flex', justifyContent: 'flex-end', gap: '6px', padding: '6px 8px', borderTop: '1px solid #f2f4f7', background: '#fcfcfd' },
  button: (kind) => ({
    height: '26px',
    padding: '0 10px',
    borderRadius: '6px',
    fontSize: '0.76rem',
    fontWeight: 600,
    cursor: 'pointer',
    border: kind === 'primary' ? '1px solid #2dce89' : kind === 'secondary' ? '1px solid #d0d5dd' : '1px solid transparent',
    background: kind === 'primary' ? '#2dce89' : kind === 'secondary' ? '#fff' : 'transparent',
    color: kind === 'primary' ? '#fff' : '#475467',
  }),
  link: { border: 0, background: 'transparent', padding: 0, fontSize: '0.72rem', fontWeight: 600, color: '#475467', cursor: 'pointer' },
};

const DIFF_STYLES = {
  removed: { textDecoration: 'line-through', color: '#b42318', background: '#fef3f2' },
  added: { color: '#085d3a', background: '#d1fadf', borderRadius: '2px' },
};

/** One change, inline: removed text struck through, added text highlighted. */
export function DiffText({ before, after }) {
  return (
    <div style={styles.diff}>
      {diffWords(before, after).map((part, index) =>
        part.type === 'same' ? (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index}>{part.text}</span>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index} style={DIFF_STYLES[part.type]}>
            <span className="sr-only">{part.type === 'removed' ? '[removed] ' : '[added] '}</span>
            {part.text}
          </span>
        ),
      )}
    </div>
  );
}

DiffText.propTypes = { before: PropTypes.string, after: PropTypes.string };

function ChangeBody({ request, selectedSections, onToggleChange, onToggleSection, disabled = false }) {
  const { before, after, sections, changes, edits } = request;
  const selectable = request.selectableChanges && changes?.length > 0;
  return (
    <div style={styles.body}>
      {sections?.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {sections.map((section) => (
            <span key={section} style={styles.chip}>
              {section}
            </span>
          ))}
        </div>
      )}
      {selectable && (
        <div>
          <div style={{ ...styles.meta, marginBottom: '4px' }}>Choose which changes and sections to apply.</div>
          {changes.map((change, changeIndex) => {
            const selected = selectedSections[changeIndex] || [];
            const anySelected = selected.some(Boolean);
            return (
              <div key={`${changeIndex}-${change.summary}`} style={styles.selectableChange}>
                <div style={styles.changeHeader}>
                  <span style={{ ...styles.changeSummary, textDecoration: anySelected ? 'none' : 'line-through', color: anySelected ? '#101828' : '#98a2b3' }}>
                    {change.summary}
                  </span>
                  <button
                    type="button"
                    style={styles.toggleChange}
                    disabled={disabled}
                    aria-label={anySelected ? `Remove change: ${change.summary}` : `Keep change: ${change.summary}`}
                    onClick={() => onToggleChange(changeIndex)}
                  >
                    {anySelected ? 'Remove' : 'Keep'}
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '5px' }}>
                  {(change.sections || []).map((section, sectionIndex) => (
                    <button
                      key={`${sectionIndex}-${section}`}
                      type="button"
                      style={styles.selectableChip(Boolean(selected[sectionIndex]))}
                      disabled={disabled}
                      aria-pressed={Boolean(selected[sectionIndex])}
                      aria-label={`${selected[sectionIndex] ? 'Remove' : 'Restore'} ${section}`}
                      onClick={() => onToggleSection(changeIndex, sectionIndex)}
                    >
                      {section}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {!selectable && changes?.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: '16px', fontSize: '0.8rem', lineHeight: 1.4 }}>
          {changes.map((change) => (
            <li key={`${change.summary}-${(change.sections || []).join('|')}`}>
              {change.summary}
              {change.sections?.length > 0 && <span style={{ color: '#667085' }}> · {change.sections.join(', ')}</span>}
            </li>
          ))}
        </ul>
      )}
      {edits?.map((edit) => (
        <div key={edit.label}>
          <div style={styles.label}>{edit.label}</div>
          <DiffText before={edit.before} after={edit.after} />
        </div>
      ))}
      {after !== undefined && <DiffText before={before} after={after} />}
    </div>
  );
}

ChangeBody.propTypes = {
  request: PropTypes.object.isRequired,
  selectedSections: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.bool)),
  onToggleChange: PropTypes.func,
  onToggleSection: PropTypes.func,
  disabled: PropTypes.bool,
};

/**
 * What a chat tool is about to change, and the clinician's answer: Allow
 * (once), Always allow (this tool stops asking), or Deny. Once answered it
 * folds to a one-line record that can be opened again.
 */
export default function PermissionCard({ request, decision, onDecide }) {
  const [open, setOpen] = useState(false);
  const [selectedSections, setSelectedSections] = useState(() =>
    (request.changes || []).map((change) => (change.sections || []).map(() => true)),
  );
  const { title, destination, description, permissionLabel } = request;
  const decided = decision ? DECIDED[decision] : null;
  const meta = [destination, description].filter(Boolean).join(' · ');
  const selectable = request.selectableChanges && request.changes?.length > 0;
  const selectedChanges = selectable
    ? request.changes
      .map((change, changeIndex) => ({
        ...change,
        sections: (change.sections || []).filter((section, sectionIndex) => selectedSections[changeIndex]?.[sectionIndex]),
      }))
      .filter((change) => change.sections.length > 0)
    : [];
  const toggleChange = (changeIndex) =>
    setSelectedSections((current) => current.map((sections, index) =>
      index === changeIndex ? sections.map(() => !sections.some(Boolean)) : sections,
    ));
  const toggleSection = (changeIndex, sectionIndex) =>
    setSelectedSections((current) => current.map((sections, index) =>
      index === changeIndex
        ? sections.map((selected, itemIndex) => itemIndex === sectionIndex ? !selected : selected)
        : sections,
    ));
  return (
    <div role="group" aria-label={title} style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>
          {title}
          {meta && <div style={styles.meta}>{meta}</div>}
        </div>
        {decided && (
          <span aria-live="polite" style={styles.pill(decided)}>
            {decided.label}
          </span>
        )}
      </div>
      {(!decided || open) && (
        <ChangeBody
          request={request}
          selectedSections={selectedSections}
          onToggleChange={toggleChange}
          onToggleSection={toggleSection}
          disabled={Boolean(decided)}
        />
      )}
      {decided ? (
        <div style={{ padding: '0 10px 8px', marginTop: open ? 0 : '-4px' }}>
          <button type="button" style={styles.link} aria-expanded={open} onClick={() => setOpen((value) => !value)}>
            {open ? 'Hide change' : 'Show change'}
          </button>
        </div>
      ) : (
        <div style={styles.footer}>
          <button type="button" style={styles.button('ghost')} onClick={() => onDecide(PERMISSION_DECISIONS.deny)}>
            {selectable ? 'Cancel' : 'Deny'}
          </button>
          {!request.requireReview && (
            <button
              type="button"
              style={styles.button('secondary')}
              title={`Don’t ask again for: ${permissionLabel || title}`}
              onClick={() => onDecide(PERMISSION_DECISIONS.always)}
            >
              Always allow
            </button>
          )}
          <button
            type="button"
            style={{ ...styles.button('primary'), opacity: selectable && selectedChanges.length === 0 ? 0.55 : 1 }}
            disabled={selectable && selectedChanges.length === 0}
            onClick={() => onDecide(selectable
              ? { decision: PERMISSION_DECISIONS.once, selectedChanges }
              : PERMISSION_DECISIONS.once)}
          >
            {selectable ? 'Apply selected' : 'Allow'}
          </button>
        </div>
      )}
    </div>
  );
}

PermissionCard.propTypes = {
  request: PropTypes.shape({
    title: PropTypes.string.isRequired,
    destination: PropTypes.string,
    description: PropTypes.string,
    before: PropTypes.string,
    after: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.string),
    changes: PropTypes.arrayOf(PropTypes.shape({
      summary: PropTypes.string,
      directive: PropTypes.string,
      sections: PropTypes.arrayOf(PropTypes.string),
    })),
    selectableChanges: PropTypes.bool,
    requireReview: PropTypes.bool,
    edits: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, before: PropTypes.string, after: PropTypes.string })),
    permissionLabel: PropTypes.string,
  }).isRequired,
  decision: PropTypes.oneOf(Object.values(PERMISSION_DECISIONS)),
  onDecide: PropTypes.func.isRequired,
};

/**
 * A question from the agent with clickable answers (ask_user), plus a box for
 * something else. Once answered it shows what was chosen.
 */
export function QuestionCard({ question, options, answer, onAnswer }) {
  const [typing, setTyping] = useState(false);
  const [text, setText] = useState('');
  const submitText = () => text.trim() && onAnswer({ answer: text.trim(), choseOption: false });
  return (
    <div role="group" aria-label={question} style={styles.card}>
      <div style={{ ...styles.header, paddingBottom: answer ? '8px' : '6px' }}>
        <div style={styles.title}>{question}</div>
      </div>
      {answer ? (
        <div style={{ padding: '0 10px 8px', fontSize: '0.8rem', color: '#475467' }} aria-live="polite">
          <i className="fa fa-check" aria-hidden="true" style={{ color: '#067647', marginRight: '6px' }} />
          {answer.cancelled ? 'No answer' : answer.answer}
        </div>
      ) : (
        <div style={{ padding: '0 8px 8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {options.map((option, index) => (
            <button
              key={option.label}
              type="button"
              onClick={() => onAnswer({ answer: option.label, choseOption: true })}
              style={{
                textAlign: 'left',
                border: '1px solid #e4e7ec',
                borderRadius: '8px',
                background: '#fff',
                padding: '6px 10px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'baseline',
                gap: '8px',
              }}
            >
              <span aria-hidden="true" style={{ fontSize: '0.72rem', color: '#98a2b3', fontWeight: 600, minWidth: '12px' }}>
                {index + 1}
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#101828' }}>{option.label}</span>
                {option.description && (
                  <span style={{ display: 'block', fontSize: '0.74rem', color: '#667085', lineHeight: 1.35 }}>{option.description}</span>
                )}
              </span>
            </button>
          ))}
          {typing ? (
            <div style={{ display: 'flex', gap: '6px' }}>
              <input
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && submitText()}
                placeholder="Type your answer"
                aria-label="Your answer"
                style={{ flex: 1, minWidth: 0, border: '1px solid #d0d5dd', borderRadius: '6px', padding: '4px 8px', fontSize: '0.8rem' }}
              />
              <button type="button" style={styles.button('primary')} onClick={submitText} disabled={!text.trim()}>
                Send
              </button>
            </div>
          ) : (
            <button type="button" style={{ ...styles.link, alignSelf: 'flex-start', padding: '2px 2px' }} onClick={() => setTyping(true)}>
              Something else…
            </button>
          )}
        </div>
      )}
    </div>
  );
}

QuestionCard.propTypes = {
  question: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string.isRequired, description: PropTypes.string })).isRequired,
  answer: PropTypes.shape({ answer: PropTypes.string, cancelled: PropTypes.bool }),
  onAnswer: PropTypes.func.isRequired,
};

/** The turn's tool actions, kept with the answer (and saved with it), each one undoable. */
export function AgentActions({ actions, onUndo = null }) {
  // Answered questions are kept for the agent's memory, not shown as changes
  const changes = (actions || []).filter((action) => action.status !== 'answered');
  if (!changes.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '6px' }}>
      {changes.map((action, index) => (
        <span
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          style={{ fontSize: '0.76rem', color: action.status === 'done' ? '#067647' : '#9a6700', display: 'flex', gap: '6px', alignItems: 'center' }}
        >
          <i className={action.status === 'done' ? 'fa fa-check' : 'fa fa-exclamation-circle'} aria-hidden="true" />
          <span style={{ minWidth: 0 }}>{action.summary}</span>
          {onUndo && action.undo && action.id && (
            <button type="button" style={styles.link} onClick={() => onUndo(action)} aria-label={`Undo: ${action.summary}`}>
              Undo
            </button>
          )}
        </span>
      ))}
    </div>
  );
}

AgentActions.propTypes = {
  actions: PropTypes.arrayOf(PropTypes.shape({ summary: PropTypes.string, status: PropTypes.string })),
  onUndo: PropTypes.func,
};

/** The tools this clinician said "Always allow" to, each one revocable. */
export function RememberedTools({ remembered, onForget }) {
  const entries = Object.entries(remembered || {});
  if (entries.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', fontSize: '0.72rem', color: '#667085', margin: '0 0 8px' }}>
      <span>Always allowed:</span>
      {entries.map(([permissionKey, { label }]) => (
        <span key={permissionKey} style={{ ...styles.chip, display: 'inline-flex', alignItems: 'center', gap: '2px', paddingRight: '2px' }}>
          {label}
          <button
            type="button"
            aria-label={`Ask again before: ${label}`}
            title="Ask me again"
            onClick={() => onForget(permissionKey)}
            style={{ border: 0, background: 'transparent', cursor: 'pointer', color: '#667085', padding: '0 4px', lineHeight: 1 }}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

RememberedTools.propTypes = {
  remembered: PropTypes.objectOf(PropTypes.shape({ label: PropTypes.string })),
  onForget: PropTypes.func.isRequired,
};
